import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Image as ImageIcon, Upload, RotateCcw, Loader2 } from "lucide-react";
import { compressImage } from "@/utils/imageCompression";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = [
  { key: "hero_image", label: "Hero (Portada Principal)", aspect: "16/9" },
  { key: "print_image", label: "Impresión Comercial", aspect: "16/9" },
  { key: "portfolio_image", label: "Nuestros Trabajos", aspect: "16/9" },
];

// Resolve any stored image reference to a browser-safe URL for preview.
const resolveImg = (u) => {
  if (!u) return "";
  if (u.startsWith("data:") || u.startsWith("http") || u.startsWith("/assets/") || u.startsWith("blob:")) return u;
  if (u.startsWith("/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return `${process.env.REACT_APP_BACKEND_URL}/${u}`;
};

const LandingImagesCard = () => {
  const [images, setImages] = useState({ hero_image: "", print_image: "", portfolio_image: "" });
  const [uploadingKey, setUploadingKey] = useState(null);
  const fileInputs = useRef({});

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const r = await axios.get(`${API}/public/landing-images`);
      setImages({ hero_image: "", print_image: "", portfolio_image: "", ...(r.data || {}) });
    } catch (err) {
      console.error("Error fetching landing images:", err);
    }
  };

  const triggerFile = (key) => {
    if (fileInputs.current[key]) {
      fileInputs.current[key].value = "";
      fileInputs.current[key].click();
    }
  };

  const handleUpload = async (key, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecciona un archivo de imagen válido");
      return;
    }
    setUploadingKey(key);
    try {
      const token = localStorage.getItem("token");
      // Compress client-side and store as base64 data URL directly in DB (persistent across deploys)
      const { dataUrl, sizeKB } = await compressImage(file, { maxWidth: 1800, maxHeight: 1800, quality: 0.82 });
      if (sizeKB > 800) {
        // Second pass with lower quality if too large
        const second = await compressImage(file, { maxWidth: 1400, maxHeight: 1400, quality: 0.7 });
        var finalUrl = second.dataUrl;
      } else {
        finalUrl = dataUrl;
      }

      const res = await axios.put(
        `${API}/admin/landing-images`,
        { [key]: finalUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setImages({ hero_image: "", print_image: "", portfolio_image: "", ...(res.data || {}) });
      toast.success("Imagen actualizada correctamente");
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(err.response?.data?.detail || "Error al subir imagen");
    } finally {
      setUploadingKey(null);
    }
  };

  const handleReset = async (key) => {
    setUploadingKey(key);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(
        `${API}/admin/landing-images`,
        { [key]: "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setImages({ hero_image: "", print_image: "", portfolio_image: "", ...(res.data || {}) });
      toast.success("Imagen restaurada al valor por defecto");
    } catch (err) {
      console.error("Reset error:", err);
      toast.error("Error al restaurar imagen");
    } finally {
      setUploadingKey(null);
    }
  };

  return (
    <Card className="mb-8 border-amber-200 bg-gradient-to-br from-amber-50/60 to-orange-50/50" data-testid="landing-images-card">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-amber-600" />
          <CardTitle className="text-lg text-amber-900">Imágenes de Landing</CardTitle>
        </div>
        <p className="text-sm text-amber-800/70 mt-1">
          Cambia las imágenes principales de la página pública: Hero, Impresión Comercial y Nuestros Trabajos. Se almacenan en la base de datos (persistentes entre deploys).
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SLOTS.map(slot => {
            const value = images[slot.key];
            const isBusy = uploadingKey === slot.key;
            return (
              <div
                key={slot.key}
                className="bg-white rounded-xl border border-amber-200 p-3 shadow-sm"
                data-testid={`landing-image-slot-${slot.key}`}
              >
                <p className="text-xs font-bold text-stone-700 mb-2 tracking-wide uppercase">{slot.label}</p>
                <div
                  className="relative w-full rounded-lg overflow-hidden bg-stone-100 border border-stone-200 flex items-center justify-center"
                  style={{ aspectRatio: slot.aspect }}
                >
                  {value ? (
                    <img
                      src={resolveImg(value)}
                      alt={slot.label}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center text-stone-400 px-3">
                      <ImageIcon className="w-8 h-8 mx-auto mb-1 opacity-40" />
                      <p className="text-[11px]">Usando imagen por defecto</p>
                    </div>
                  )}
                  {isBusy && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    </div>
                  )}
                </div>
                <input
                  ref={el => { fileInputs.current[slot.key] = el; }}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleUpload(slot.key, e.target.files?.[0])}
                  data-testid={`landing-image-input-${slot.key}`}
                />
                <div className="mt-2 flex gap-1.5">
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => triggerFile(slot.key)}
                    disabled={isBusy}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white text-xs h-8"
                    data-testid={`landing-image-upload-${slot.key}`}
                  >
                    <Upload className="w-3.5 h-3.5 mr-1" /> Cambiar
                  </Button>
                  {value && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReset(slot.key)}
                      disabled={isBusy}
                      className="h-8 px-2 text-xs"
                      data-testid={`landing-image-reset-${slot.key}`}
                      title="Restaurar valor por defecto"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default LandingImagesCard;
